import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BatchUpdateFileType } from '@app/app.constants';
import { MatSnackBar } from '@angular/material/snack-bar';;
import { BatchUpdateContextWeekTypeHistory, JsonBatchUpdateContextWeekTypeHistory } from '@core/models/batch-update-context-week-type-history.model';
import { BatchUpdatePriceHistory } from '@core/models/batch-update-price-history.model';
import { BatchUpdateStepName } from '@core/models/batch-update-status.model';
import {
  BatchUpdateValidationData,
  BatchUpdateValidationSummary,
  DayToValidate,
  JsonBathUpdateValidationData,
  JsonBatchUpdateValidationSummary,
  JsonDayToValidate,
} from '@core/models/batch-update-validation.model';
import { BatchUpdateCorrectionData, JsonBatchUpdateCorrectionData } from '@core/models/batch-update.model';
import { FileModel, IFileModel } from '@core/models/file.model';
import { AuthService } from '@core/services/auth.service';
import { BaseService } from '@core/services/base/base.service';
import { ErrorHandlerService, HttpError } from '@core/services/error-handler.service';
import { environment } from '@env/environment';
import moment from 'moment';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export enum UpdateType {
  PriceUpdate = 1,
  BroadcastTimeUpd = 2,
}

export interface BatchUpdateFilter {
  fileName: string;
  channel: number;
  ut?: string;
  fileType?: number;
}

export interface GridWeekTypeFilter {
  idTvChannel: number;
  fileName: string;
  startDate: string;
  endDate: string;
  ut?: string;
}

interface ThemaValidationSummaryResponse {
  data: {
    'validation-summary': JsonBatchUpdateValidationSummary[];
    'validation-summary-by-day': JsonDayToValidate[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class BatchUpdateService extends BaseService {
  private selectedFileTypeByChannel = new Map<number, number>();
  public uploadProgress = new BehaviorSubject(0);
  public routeIntegrateContextGridWeekTypeLink = '/batch-update-contexts-week-type';
  public routeBatchUpdatePricesLink = '/batch-update-prices';
  public routeBatchUpdatePricesByDateLink = '/batch-update-prices-by-date';
  public routeBatchUpdateContextsByDateLink = '/batch-update-contexts-by-date';

  constructor(
    private httpService: HttpClient,
    private snackBar: MatSnackBar,
    private errorHandlerService: ErrorHandlerService,
    private authService: AuthService
  ) {
    super();
  }

  getSelectedFileType(idTvChannel: number): number | undefined {
    return this.selectedFileTypeByChannel.get(idTvChannel);
  }

  setSelectedFileType(idTvChannel: number, fileTypeId: number): void {
    this.selectedFileTypeByChannel.set(idTvChannel, fileTypeId);
  }

  importNbcBreakFile(fileName: string, channelCode) {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Le fichier est introuvable.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];
    const url = environment.screen_spot_microservice_base_url + '/batch-update-thema/breakfiles/import';

    return new Observable(observer => {
      this.httpService.post(url, { fileName, channelCode }).subscribe(
        (response: any) => observer.next(response),
        (error: any) => {
          if (error.status != 504 || error.status != 0) {
            if (error.status !== 200) {
              this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            } else {
              observer.next(fileName);
            }
          }
          observer.error(error);
        }
      );
    });
  }

  importNbcProgFile(fileName: string, channelCode) {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Le fichier est introuvable.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];
    const filter = { fileName, channelCode };

    let url = environment.screen_spot_microservice_base_url + '/batch-update-contexts-nbc/integrate';

    url += '?' + this.getParamsFromFilter(filter);

    return new Observable(observer => {
      this.httpService.post(url, {}).subscribe(
        (response: any) => observer.next(response),
        (error: any) => {
          if (error.status != 504 || error.status != 0) {
            if (error.status !== 200) {
              this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            } else {
              observer.next(fileName);
            }
          }
          observer.error(error);
        }
      );
    });
  }

  startBatchUpdateStep(filter: BatchUpdateFilter, stepName: string) {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de faire la.' + stepName,
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];
    const user = this.authService.currentUserData ? this.authService.currentUserData.sub : '';
    const url = environment.screen_spot_microservice_base_url + this.getBatchUpdateStepUrl(stepName);
    const body = { idTvChannel: filter.channel, fileName: filter.fileName, ut: user.toUpperCase() };

    return new Observable(observer => {
      this.httpService.post(url, body).subscribe(
        (response: any) => observer.next(response),
        (error: any) => {
          if (error.status != 504 && error.status != 0) {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
          }
          observer.error(error);
        }
      );
    });
  }

  getBatchUpdateCorrectionData(idTvChannel: number) {
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Une erreur  est survenue lors de recuperation de données à corriger. Veuillez essayer plus tard.',
      },
    ];
    const filter = {
      idTvChannel: idTvChannel,
    };
    let url = environment.screen_spot_microservice_base_url + '/batch-update-thema/rows';

    url += '?' + this.getParamsFromFilter(filter);

    return new Observable<BatchUpdateCorrectionData[]>(observer => {
      this.httpService
        .get(url, {})
        .pipe(
          map(response => {
            const rows = response['data']?.rows ?? response['data'];
            if (Array.isArray(rows) && rows.length > 0) {
              return rows.map(
                channelBatchUpdateData => new BatchUpdateCorrectionData(channelBatchUpdateData)
              );
            }
            return [];
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  updateBatchUpdateCorrectionDataElement(id: number, idTvChannel: number, propertyToUpdate: {}) {
    if (!id || !propertyToUpdate) {
      return;
    }
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Une erreur  est survenue lors de correction de données. Veuillez essayer plus tard.',
      },
    ];
    const url = environment.screen_spot_microservice_base_url + '/batch-update-thema/rows';

    return new Observable<BatchUpdateCorrectionData>(observer => {
      this.httpService
        .patch(url, { idTvChannel, changes: [{ id, ...propertyToUpdate }] })
        .pipe(
          map(response => {
            const rows = response['data']?.rows ?? response['data'];
            const updatedRow = Array.isArray(rows) ? rows.find(row => row.id === id) : rows;
            return updatedRow?.id ? new BatchUpdateCorrectionData(updatedRow as JsonBatchUpdateCorrectionData) : null;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  updateListOfBatchUpdateLines(batchUpdateLines: BatchUpdateValidationData[], updateType?: UpdateType) {
    let linesToUpdate = this.prepareValidationDataForUpdate(batchUpdateLines, updateType);
    if (linesToUpdate.length > 0) {
      const httpErrors: HttpError[] = [
        {
          statusCode: 500,
          message: 'Une erreur  est survenue lors de la modification des données. Veuillez essayer plus tard.',
        },
      ];
      const url = environment.screen_spot_microservice_base_url + '/batch-update-thema/rows';
      return new Observable<BatchUpdateValidationData[]>(observer => {
        this.httpService
          .patch(url, { idTvChannel: batchUpdateLines[0].idTvChannel, changes: linesToUpdate })
          .pipe(
            map(response => {
              const rows = response['data']?.rows ?? response['data'];
              if (Array.isArray(rows) && rows.length > 0) {
                return rows.map(
                  channelBatchUpdateData => new BatchUpdateValidationData(channelBatchUpdateData)
                );
              }
              return [];
            })
          )
          .subscribe(
            (response: any) => observer.next(response),
            error => {
              this.errorHandlerService.showErrorMessage(httpErrors, error.status);
              observer.error(error);
            }
          );
      });
    }
    return of(null);
  }

  downloadBatchUpdateDataForCorrection(idTvChannel: number) {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Pas de données à télécharger.',
      },
      {
        statusCode: 500,
        message: 'Impossible de télécharger le fichier. Veuillez essayer plus tard.',
      },
    ];
    const filter = {
      idTvChannel: idTvChannel,
    };
    let url = environment.screen_spot_microservice_base_url + '/batch-update-thema/csv/correction';
    url += '?' + this.getParamsFromFilter(filter);

    return new Observable<IFileModel>(observer => {
      this.httpService
        .get(url, { reportProgress: true, responseType: 'blob' })
        .pipe(
          map(
            res =>
              new FileModel({
                fileName: `export_batch_update_data_for_correction_${moment().format('YYYYMMDD')}.csv`,
                data: res,
              })
          )
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  downloadBatchUpdateDataForValidation(idTvChannel: number, broadcastDate: string) {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Pas de données de validaiton à télécharger.',
      },
      {
        statusCode: 500,
        message: 'Impossible de télécharger le fichier. Veuillez essayer plus tard.',
      },
    ];
    const filter = {
      idTvChannel: idTvChannel,
      broadcastDate: broadcastDate,
    };
    let url = environment.screen_spot_microservice_base_url + '/batch-update-thema/csv/validation';
    url += '?' + this.getParamsFromFilter(filter);

    return new Observable<IFileModel>(observer => {
      this.httpService
        .get(url, { reportProgress: true, responseType: 'blob' })
        .pipe(
          map(
            res =>
              new FileModel({
                fileName: `export_batch_update_data_for_validation_${moment().format('YYYYMMDD')}.csv`,
                data: res,
              })
          )
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  getBatchUpdateStepUrl(stepName: string): string {
    switch (stepName) {
      case BatchUpdateStepName.Integration:
        return '/batch-update-thema/integrate';
      case BatchUpdateStepName.Transformation:
        return '/batch-update-thema/steps/2.0';
      case BatchUpdateStepName.DuplicateCheck:
        return '/batch-update-thema/steps/3.1';
      case BatchUpdateStepName.ClosureDateCheck:
        return '/batch-update-thema/steps/3.2';
      case BatchUpdateStepName.DataPreparation:
        return '/batch-update-thema/steps/5.0';
      case BatchUpdateStepName.BroadcastTimeAutoChange:
        return '/batch-update-thema/steps/5.1';
      case BatchUpdateStepName.Validation:
        return '/batch-update-thema/steps/6.0';
      case BatchUpdateStepName.ReportGeneration:
        return '/batch-update-thema/steps/6.1';
      case BatchUpdateStepName.FlashGeneration:
        return '/batch-update-thema/steps/6.2';
      case BatchUpdateStepName.BreakIdOrder:
        return '/batch-update-thema/steps/6.5';
      case BatchUpdateStepName.DataBaseUpdate:
        return '/batch-update-thema/steps/7.0';
      default:
        return '';
    }
  }

  getValidationDataAndSummary(idTvChannel: number, broadcastDate?: string) {
    const batchUpdateValidationSummaryObs = this.getValidationSummary(idTvChannel);
    const batchUpdateValidationDataObs = this.getValidationData(idTvChannel, broadcastDate);

    return forkJoin([batchUpdateValidationSummaryObs, batchUpdateValidationDataObs]);
  }

  getValidationData(idTvChannel: number, broadcastDate?: string) {
    let dataFilter: any = {
      idTvChannel: idTvChannel,
    };
    if (broadcastDate) {
      dataFilter = {
        idTvChannel: idTvChannel,
        broadcastDate: broadcastDate,
      };
    }
    return this.httpService
      .get(environment.screen_spot_microservice_base_url + '/batch-update-thema/validation?' + this.getParamsFromFilter(dataFilter), {})
      .pipe(
        map(response => {
          const rows = response['data']?.rows ?? response['data'];
          if (Array.isArray(rows)) {
            return rows.map(row => new BatchUpdateValidationData(row));
          }
          throw new Error('Erreur lors de la récupération des données à valider : pas de données.');
        })
      );
  }

  getValidationSummary(idTvChannel: number) {
    const summaryFilter = {
      idTvChannel: idTvChannel,
    };

    const url = environment.screen_spot_microservice_base_url + '/batch-update-thema';
    const query = '?' + this.getParamsFromFilter(summaryFilter);
    return this.httpService.get<ThemaValidationSummaryResponse>(url + '/summary' + query).pipe(
      map(response => {
        const summary = response.data?.['validation-summary']?.[0];
        const days = response.data?.['validation-summary-by-day'];
        if (!summary || !Array.isArray(days)) {
          throw new Error('Erreur lors de la récupération du résumé sur la période. Le résumé est vide.');
        }
        const result = new BatchUpdateValidationSummary(summary);
        result.daysToValidate = days.map(day => new DayToValidate(day)).filter(day => !!day.broadcastDate);
        return result;
      })
    );
  }

  prepareValidationDataForUpdate(validationDataLines: BatchUpdateValidationData[], updateType?: UpdateType): any[] {
    let lines = [];
    if (validationDataLines && validationDataLines.length) {
      lines = validationDataLines
        .map(batchUpdateLine => {
          if (batchUpdateLine && batchUpdateLine.idBatchUpdate) {
            switch (updateType) {
              case UpdateType.PriceUpdate:
                return { id: batchUpdateLine.idBatchUpdate, grossPrice: batchUpdateLine.price, captifPrice: batchUpdateLine.captifPrice };
              case UpdateType.BroadcastTimeUpd:
                return {
                  id: batchUpdateLine.idBatchUpdate,
                  broadcastTimeUpd: batchUpdateLine.broadcastTimeNew,
                  isDuplicate: false,
                };
              default:
                return { id: batchUpdateLine.idBatchUpdate, isUserApproved: true, durationUpd: batchUpdateLine.durationNew };
            }
          }
        })
        .filter(bu => bu && bu.id);
    }
    return lines;
  }

  getNewScreensWithoutPrices(idTvChannel: number, minNotIgnoredDate: string) {
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Une erreur  est survenue lors de recuperation de données à corriger. Veuillez essayer plus tard.',
      },
    ];
    const filter = {
      idTvChannel: idTvChannel,
      minNotIgnoredDate: minNotIgnoredDate,
    };
    let url = environment.screen_spot_microservice_base_url + '/batch-update-thema/validation/unpriced';

    url += '?' + this.getParamsFromFilter(filter);

    return new Observable<BatchUpdateValidationData[]>(observer => {
      this.httpService
        .get(url, {})
        .pipe(
          map(response => {
            const rows = response['data']?.rows ?? response['data'];
            if (Array.isArray(rows)) {
              let data: BatchUpdateValidationData[] = rows.map(
                (valDataJson: JsonBathUpdateValidationData) => {
                  return new BatchUpdateValidationData(valDataJson);
                }
              );
              return data;
            }
            return null;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  changeBreakCodes(
    validationDataLines: BatchUpdateValidationData[],
    valLinesWithNewBreakCodes: BatchUpdateValidationData[],
    idTvChannel,
    broadcastDate
  ) {
    const breakCodes = validationDataLines
      .map(valLine => {
        if (valLine.idBatchUpdate) {
          return valLine.breakCode;
        }
      })
      .filter(breakCode => breakCode);

    const newBreakCodes = valLinesWithNewBreakCodes.map(valLine => valLine.breakCode);
    const data = {
      listOfBreakCodes: breakCodes,
      listOfNewBreakCodes: newBreakCodes,
      broadcastDate: broadcastDate,
      idTvChannel: idTvChannel,
    };

    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Une erreur  est survenue lors de la modification des intitulés. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.screen_spot_microservice_base_url + '/batch-update-thema/break-codes';
    return new Observable<BatchUpdateValidationData[]>(observer => {
      this.httpService.patch(url, data).subscribe(
        (response: any) => observer.next(response),
        error => {
          this.errorHandlerService.showErrorMessage(httpErrors, error.status);
          observer.error(error);
        }
      );
    });
  }

  public integrateContextGridWeekType(filter: GridWeekTypeFilter) {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Le fichier est introuvable.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];
    const contextFilter = {
      fileName: filter.fileName,
      idTvChannel: filter.idTvChannel,
      startDate: filter.startDate,
      endDate: filter.endDate,
      user: this.authService.currentUserData ? this.authService.currentUserData.sub : '',
    };

    const url =
      environment.screen_spot_microservice_base_url +
      this.routeIntegrateContextGridWeekTypeLink +
      '?' +
      this.getParamsFromFilter(contextFilter);

    return new Observable(observer => {
      this.httpService.post(url, {}).subscribe(
        (response: any) => observer.next(response),
        (error: any) => {
          if (error.status != 504 || error.status != 0) {
            if (error.status !== 200) {
              this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            }
          }
          observer.error(error);
        }
      );
    });
  }

  public batchUpdatePrices(filter: GridWeekTypeFilter) {
    filter.ut = this.authService.currentUserData ? this.authService.currentUserData.sub : '';

    const priceFilter = {
      fileName: filter.fileName,
      idTvChannel: filter.idTvChannel,
      user: filter.ut,
      startDate: filter.startDate,
      endDate: filter.endDate,
    };

    let url = environment.screen_spot_microservice_base_url + this.routeBatchUpdatePricesLink + '?' + this.getParamsFromFilter(priceFilter);

    return new Observable(observer => {
      this.httpService.post(url, {}).subscribe(
        (response: any) => observer.next(response),
        (error: any) => this.handleBatchUpdatePriceError(error, observer)
      );
    });
  }

  public batchUpdatePricesByDate(fileName: string, idTvChannel: number) {
    const user = this.authService.currentUserData ? this.authService.currentUserData.sub : '';
    const priceFilter = {
      fileName: fileName,
      idTvChannel: idTvChannel,
      user: user,
    };

    let url =
      environment.screen_spot_microservice_base_url +
      this.routeBatchUpdatePricesByDateLink +
      '?' +
      this.getParamsFromFilter(priceFilter);

    return new Observable(observer => {
      this.httpService.post(url, {}).subscribe(
        (response: any) => observer.next(response),
        (error: any) => this.handleBatchUpdatePriceError(error, observer)
      );
    });
  }

  public batchUpdateContextsByDate(fileName: string, idTvChannel: number) {
    const contextFilter = {
      fileName,
      idTvChannel,
      user: this.authService.currentUserData ? this.authService.currentUserData.sub : '',
    };
    const url =
      environment.screen_spot_microservice_base_url +
      this.routeBatchUpdateContextsByDateLink +
      '?' +
      this.getParamsFromFilter(contextFilter);

    return new Observable(observer => {
      this.httpService.post(url, {}).subscribe(
        (response: any) => observer.next(response),
        (error: any) => {
          if (error.status !== 504 && error.status !== 0) {
            const errorMessage =
              error.error?.data?.error ??
              error.error?.message ??
              error.error?.detail ??
              'Une erreur est survenue. Veuillez essayer plus tard.';
            this.errorHandlerService.showErrorMessage([], error.status, errorMessage);
          }
          observer.error(error);
        }
      );
    });
  }

  private handleBatchUpdatePriceError(error: any, observer): void {
    if (error.status === 504 || error.status === 0) {
      observer.next(null);
      return;
    }

    const errorMessage =
      error.error?.data?.error ??
      error.error?.message ??
      error.error?.detail ??
      'Une erreur est survenue. Veuillez essayer plus tard.';

    this.errorHandlerService.showErrorMessage([], error.status, errorMessage);
    observer.error(error);
  }

  public getBatchUpdatePriceHistory(filter) {
    const url = environment.screen_spot_microservice_base_url + '/batch-update-price-logs?' + this.getParamsFromFilter(filter);

    return this.httpService.get(url).pipe(
      map((response: any) => ({
        logs: response?.data?.logs?.map(log => new BatchUpdatePriceHistory(log)) ?? [],
        resultCount: response?.data?.totalCount ?? 0,
      }))
    );
  }

  public getBatchUpdateContextWeekTypeHistory(filter: {
    idTvChannel: number;
    idFileType: number;
    page: number;
    itemsPerPage: number;
    searchElement?: string;
  }): Observable<{ logs: BatchUpdateContextWeekTypeHistory[]; resultCount: number }> {
    const url = environment.screen_spot_microservice_base_url + '/batch-update-context-logs?' + this.getParamsFromFilter(filter);

    return this.httpService.get<{ data: { logs: JsonBatchUpdateContextWeekTypeHistory[]; totalCount: number } }>(url).pipe(
      map(response => ({
        logs: response.data.logs.map(log => new BatchUpdateContextWeekTypeHistory(log)),
        resultCount: response.data.totalCount,
      }))
    );
  }

  public downloadBatchUpdatePriceReport(fileName: string, fileType: number): Observable<IFileModel> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: "Le fichier n'est pas trouvé.",
      },
      {
        statusCode: 500,
        message: 'Impossible de télécharger le fichier. Veuillez essayer plus tard.',
      },
    ];
    const filter = { fileName, fileType };
    const url = environment.screen_spot_microservice_base_url + '/download-report-from-s3?' + this.getParamsFromFilter(filter);

    return new Observable<IFileModel>(observer => {
      const validFileTypes = [
        BatchUpdateFileType.gridContext,
        BatchUpdateFileType.gridContextWeekType,
        BatchUpdateFileType.pricesWeekType,
        BatchUpdateFileType.gridContextDateBased,
        BatchUpdateFileType.pricesDateBased,
      ];
      if (!fileName?.trim() || !validFileTypes.includes(fileType)) {
        this.errorHandlerService.showErrorMessage(httpErrors, 400);
        observer.error(new Error('Invalid fileType or fileName for report download.'));
        return;
      }
      this.httpService
        .get(url, { reportProgress: true, responseType: 'blob' })
        .pipe(
          map(
            res =>
              new FileModel({
                fileName,
                data: res,
              })
          )
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }
}
